import type {
    DiscoveryGetFileUploadUrlResponse,
    ContentDetailsData,
} from "@amzn/ads-discovery-theme-service-typescript-client";
import type {
    Content,
    SalesforceAssociation,
} from "@amzn/ads-measurement-plm-service-type-script-client";
import {
    ContentAction,
    ContentType,
    SalesforceObjectType,
} from "@amzn/ads-measurement-plm-service-type-script-client";
import type {
    AsyncState,
    UseAsyncActions,
    UseAsyncMeta,
} from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { useStore } from "zustand";

import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { getPlmApiClient } from "../../api/plmApi/plmApi";
import { insightDetailsStore } from "../../store/insightDetails";
import type { SfObject } from "../../store/insightLinkage";
import { notificationStore, NotificationType } from "../../store/notification";
import { ContentPreviewType } from "../../types/content";
import {
    resolveSfObjectId,
    resolveSfObjectType,
} from "../../utils/sfObject.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export interface PitchFileUploadActionResponse {}

interface BasePitchFileUploadActionRequest {
    insightTitle: string;
    notes: string;
    accountOpportunityId: string;
    sfObject: SfObject;
    sfAccountId: string;
}

export interface PitchFileUploadActionRequestUpload
    extends BasePitchFileUploadActionRequest {
    file: File;
    contentId?: never;
}

export interface PitchFileUploadActionRequestEdit
    extends BasePitchFileUploadActionRequest {
    file?: File;
    contentId: string;
}

export type PitchFileUploadActionRequest =
    | PitchFileUploadActionRequestUpload
    | PitchFileUploadActionRequestEdit;

export function usePitchFileUploadAction(
    props: PitchFileUploadActionRequest,
): [
    AsyncState<PitchFileUploadActionResponse>,
    UseAsyncActions<PitchFileUploadActionResponse>,
    UseAsyncMeta<PitchFileUploadActionResponse>,
] {
    const {
        insightTitle,
        notes,
        accountOpportunityId,
        sfAccountId,
        sfObject,
        file,
        contentId,
    } = props;

    const [fileUploadUrlState, fileUploadUrlAction] =
        useAsync<DiscoveryGetFileUploadUrlResponse>(async () => {
            const response = await (
                await getDiscoveryApiClient()
            ).getFileUploadUrl(file!.name, contentId);
            return response;
        });

    const [uploadFileState, uploadFileAction] = useAsync(
        async (uploadUrl: string, file: File) => {
            const response = await fetch(uploadUrl, {
                method: "PUT",
                headers: {
                    "Content-Type": file.type,
                },
                body: file,
            });
            return response;
        },
    );

    const [uploadContentDetailsState, putContentDetailsAction] = useAsync(
        async (
            contentId: string,
            contentDetails: ContentDetailsData,
            accountOpportunities: string[],
        ) => {
            const response = await (
                await getDiscoveryApiClient()
            ).putContentDetails(
                contentId,
                contentDetails,
                accountOpportunities,
            );
            return response;
        },
    );

    // call getContentDetails to retrieve content metadata
    const [getContentDetailsState, getContentDetailsAction] = useAsync(
        async (contentId: string) => {
            const response = await (
                await getDiscoveryApiClient()
            ).getContentDetails(contentId, ContentPreviewType.ORIGINAL, false);
            return response;
        },
    );

    const [createActivityAssociationState, createActivityAssociationAction] =
        useAsync(
            async (
                contentMetadata: Content,
                salesforceAssociations: SalesforceAssociation[],
            ) => {
                const response = await (
                    await getPlmApiClient()
                ).createContentSfAssociation(
                    contentMetadata,
                    ContentAction.UPLOAD,
                    salesforceAssociations,
                );
                return response;
            },
            {},
        );

    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);

    const [resetInsightDetails] = useStore(insightDetailsStore, (state) => [
        state.resetState,
    ]);

    useAsyncErrorStatusNotification(fileUploadUrlState);
    useAsyncErrorStatusNotification(uploadFileState);
    useAsyncErrorStatusNotification(uploadContentDetailsState);
    useAsyncErrorStatusNotification(getContentDetailsState);
    useAsyncErrorStatusNotification(createActivityAssociationState);

    return useAsync(async () => {
        upsertNotification(
            "Uploading insight deck",
            NotificationType.LOADING,
            insightTitle,
        );

        let uploadContentId = contentId;

        // If we have a file, then upload it first
        if (file) {
            const uploadResponse = await fileUploadUrlAction.execute(file.name);
            uploadContentId = uploadResponse.contentId;

            await uploadFileAction.execute(uploadResponse.uploadUrl!, file);
        }

        const isActivityAssociated =
            resolveSfObjectType(sfObject) === SalesforceObjectType.ACTIVITY;

        const accountOpportunities: string[] = isActivityAssociated
            ? []
            : [accountOpportunityId];

        await putContentDetailsAction.execute(
            uploadContentId!,
            {
                contentTitle: insightTitle,
                contentType: "PITCH",
                visibility: "PRIVATE",
                contentNote: notes,
            },
            accountOpportunities,
        );

        const getContentDetailsResponse = await getContentDetailsAction.execute(
            uploadContentId!,
        );

        const contentMetadata: Content = {
            contentId: uploadContentId!,
            contentType: ContentType.PITCHED_CONTENT,
            contentName: getContentDetailsResponse.content!.contentTitle,
            contentURL: getContentDetailsResponse.downloadUrl,
            contentExpiryDate:
                getContentDetailsResponse.content!.autoArchiveDate,
        };

        const salesforceAssociations: SalesforceAssociation[] =
            isActivityAssociated
                ? [
                      {
                          salesforceAccountId: sfAccountId,
                          salesforceObjectId: resolveSfObjectId(sfObject),
                          salesforceObjectType: resolveSfObjectType(sfObject),
                      },
                  ]
                : [];

        await createActivityAssociationAction.execute(
            contentMetadata,
            salesforceAssociations,
        );

        upsertNotification(
            "Insight deck successfully uploaded",
            NotificationType.SUCCESS,
            insightTitle,
        );

        resetInsightDetails();

        return {} as PitchFileUploadActionResponse;
    }, {});
}
